import * as React from "react";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import Dialog from "@mui/material/Dialog";
import FormControl from "@mui/material/FormControl";
import Link from "@mui/material/Link";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { ArrowRight as ArrowRightIcon } from "@phosphor-icons/react/ArrowRight";
import { ArrowSquareOut as ExternalLinkIcon } from "@phosphor-icons/react/ArrowSquareOut";
import { CreditCard as CreditCardIcon } from "@phosphor-icons/react/CreditCard";
import { HardDrive as HardDriveIcon } from "@phosphor-icons/react/HardDrive";
import { LockOpen as LockIcon } from "@phosphor-icons/react/LockOpen";
import { MapTrifold as MapIcon } from "@phosphor-icons/react/MapTrifold";
import { Receipt as ReceiptIcon } from "@phosphor-icons/react/Receipt";
import { UserPlus as UserPlusIcon } from "@phosphor-icons/react/UserPlus";
import { UsersThree as UsersIcon } from "@phosphor-icons/react/UsersThree";
import { Warning as WarningIcon } from "@phosphor-icons/react/Warning";

import {
	ADDON_BILLING_NOTE,
	ADDON_CONFIRMATION_TITLE,
	ADDON_DESCRIPTIONS,
	ADDON_NAMES,
	BILLING_CYCLE_LABELS,
	CANCEL_CTA,
	CONFIRM_ADDON_CTA,
	DISMISS_CTA,
	FEATURE_DESCRIPTIONS,
	FEATURE_NAMES,
	getFeatureUnavailableTitle,
	LIMIT_REACHED_MESSAGES,
	LIMIT_REACHED_TITLES,
	PAID_UPGRADE_CTA,
	PLAN_LIMITS,
	PLAN_NAMES,
	RESOURCE_NAMES,
	START_SUBSCRIPTION_CTA,
	TRIAL_UPGRADE_CTA,
} from "./constants";
import type { FeatureGateDialogProps } from "./types";

const formatCurrency = (amount: number): string =>
	new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
	}).format(amount);

interface UsageDisplayProps {
	usage: { current: number; limit: number; unit?: string };
	resourceType?: "maps" | "storage" | "private-viewers";
}

function UsageDisplay({ usage, resourceType }: UsageDisplayProps) {
	return (
		<Stack
			spacing={0.5}
			sx={{ width: "100%" }}>
			<Typography
				variant="subtitle2"
				sx={{ fontWeight: 600, mb: 0.5 }}>
				Current usage
			</Typography>
			<Typography
				variant="body2"
				color="text.secondary">
				{usage.current} of {usage.limit}{" "}
				{usage.unit ?? (resourceType ? RESOURCE_NAMES[resourceType] : "")}
			</Typography>
		</Stack>
	);
}

interface PaymentMethodDisplayProps {
	billingInfo: {
		collectionMethod: "automatic" | "manual";
		paymentMethod?: {
			cardType: string;
			lastFour: string;
			expMonth: number;
			expYear: number;
		};
	};
	onManageBilling?: () => void;
	showTooltip?: boolean;
}

function PaymentMethodDisplay({
	billingInfo,
	onManageBilling,
	showTooltip = false,
}: PaymentMethodDisplayProps) {
	const paymentContent =
		billingInfo.collectionMethod === "automatic" &&
		billingInfo.paymentMethod ? (
			<>
				<CreditCardIcon size={16} />
				<Typography
					variant="caption"
					color="text.secondary">
					{billingInfo.paymentMethod.cardType} ••••{" "}
					{billingInfo.paymentMethod.lastFour} (
					{billingInfo.paymentMethod.expMonth}/
					{billingInfo.paymentMethod.expYear.toString().slice(-2)})
				</Typography>
			</>
		) : (
			<>
				<ReceiptIcon size={16} />
				<Typography
					variant="caption"
					color="text.secondary">
					Available now. We&apos;ll invoice you (net 30)
				</Typography>
			</>
		);

	const manageBillingButton = onManageBilling && (
		<Button
			variant="text"
			size="small"
			color="secondary"
			onClick={onManageBilling}
			sx={{ minWidth: "auto", p: 0.5 }}>
			Manage billing
		</Button>
	);

	return (
		<ListItem sx={{ alignItems: "center" }}>
			<Stack
				direction="row"
				alignItems="center"
				spacing={1}>
				{paymentContent}
			</Stack>
			{showTooltip && manageBillingButton ? (
				<Tooltip title="To add more than one seat, visit billing settings">
					{manageBillingButton}
				</Tooltip>
			) : (
				manageBillingButton
			)}
		</ListItem>
	);
}

export function FeatureGateDialog({
	open,
	gateType,
	resourceType,
	featureType,
	addonType,
	currentPlan,
	requiredPlan,
	isTrialUser,
	isPartnerManaged = false,
	usage,
	billingInfo,
	addonBlockInfo,
	onClose,
	onUpgrade,
	onConfirmAddon,
	onConfirmAddonBlock,
	onStartSubscription,
	onManageBilling,
}: FeatureGateDialogProps): React.JSX.Element {
	const isLimitReached = gateType === "limit-reached";
	const isAddonConfirmation = gateType === "addon-confirmation";
	const isLimitWithAddon = gateType === "limit-reached-with-addon";
	const isStandardGate = !isAddonConfirmation && !isLimitWithAddon;
	const isTrialStandard = isTrialUser && isStandardGate;
	const centerBody =
		isAddonConfirmation || isLimitWithAddon || Boolean(requiredPlan);
	const showUpgradeOption =
		isLimitWithAddon && requiredPlan && currentPlan !== "AGENCY";

	const [selectedQuantity, setSelectedQuantity] = React.useState(1);
	const [seatQuantity, setSeatQuantity] = React.useState(1);

	const title = React.useMemo(() => {
		if (isAddonConfirmation) {
			return ADDON_CONFIRMATION_TITLE;
		}
		if ((isLimitReached || isLimitWithAddon) && resourceType) {
			return LIMIT_REACHED_TITLES[resourceType];
		}
		if (featureType) {
			return getFeatureUnavailableTitle(FEATURE_NAMES[featureType]);
		}
		return "";
	}, [
		isAddonConfirmation,
		isLimitReached,
		isLimitWithAddon,
		resourceType,
		featureType,
	]);

	const message = React.useMemo(() => {
		if (isAddonConfirmation && addonType) {
			return ADDON_DESCRIPTIONS[addonType];
		}
		if ((isLimitReached || isLimitWithAddon) && resourceType && usage) {
			return LIMIT_REACHED_MESSAGES[resourceType](
				usage.current,
				usage.limit,
				usage.unit,
			);
		}
		if (featureType) {
			return FEATURE_DESCRIPTIONS[featureType];
		}
		return "";
	}, [
		isAddonConfirmation,
		addonType,
		isLimitReached,
		isLimitWithAddon,
		resourceType,
		featureType,
		usage,
	]);

	const primaryCta = React.useMemo(() => {
		if (isAddonConfirmation) return CONFIRM_ADDON_CTA;
		if (isLimitWithAddon) {
			return resourceType === "maps" ? "Add Maps" : "Add Storage";
		}
		return isTrialStandard ? START_SUBSCRIPTION_CTA : PAID_UPGRADE_CTA;
	}, [isAddonConfirmation, isLimitWithAddon, isTrialStandard, resourceType]);

	const secondaryCta =
		isAddonConfirmation || isLimitWithAddon
			? CANCEL_CTA
			: isTrialStandard
				? TRIAL_UPGRADE_CTA
				: DISMISS_CTA;
	const isNotNowCta =
		secondaryCta === DISMISS_CTA || secondaryCta === CANCEL_CTA;
	const showLeftDismiss = isTrialStandard || isNotNowCta;
	const leftDismissLabel = isTrialStandard ? DISMISS_CTA : secondaryCta;
	const handleSecondaryAction = isTrialStandard ? onUpgrade : onClose;

	const handlePrimaryAction = () => {
		if (isAddonConfirmation) {
			onConfirmAddon?.(seatQuantity);
		} else if (isLimitWithAddon) {
			onConfirmAddonBlock?.(selectedQuantity);
		} else if (isTrialStandard) {
			onStartSubscription?.();
		} else {
			onUpgrade?.();
		}
	};

	const quantityOptions = React.useMemo(() => {
		if (!addonBlockInfo) return [];
		return Array.from({ length: addonBlockInfo.maxBlocks }, (_, i) => {
			const blocks = i + 1;
			const totalUnits = addonBlockInfo.blockSize * blocks;
			return {
				value: blocks,
				label: `${totalUnits} ${addonBlockInfo.blockUnit}`,
			};
		});
	}, [addonBlockInfo]);
	const seatQuantityOptions = React.useMemo(() => {
		if (!addonType) return [];
		if (addonType === "editor-seat") {
			return Array.from({ length: 5 }, (_, i) => {
				const seats = (i + 1) * 5;
				return { value: seats, label: `${seats} seats` };
			});
		}
		return Array.from({ length: 5 }, (_, i) => {
			const seats = i + 1;
			return {
				value: seats,
				label: `${seats} seat${seats === 1 ? "" : "s"}`,
			};
		});
	}, [addonType]);

	const totalProrated = addonBlockInfo
		? addonBlockInfo.proratedAmountPerBlock * selectedQuantity
		: 0;
	const addonTotal = addonBlockInfo
		? addonBlockInfo.pricePerBlock * selectedQuantity
		: 0;
	const seatTotal = billingInfo ? billingInfo.seatPrice * seatQuantity : 0;
	const seatProratedTotal = billingInfo
		? billingInfo.proratedAmount * seatQuantity
		: 0;
	const chargeAmount = isAddonConfirmation ? seatProratedTotal : totalProrated;
	const primaryCtaLabel =
		(isLimitWithAddon || (isAddonConfirmation && billingInfo)) && chargeAmount
			? `${primaryCta} — Pay ${formatCurrency(chargeAmount)} now`
			: primaryCta;
	const billingCycleLabel = billingInfo
		? BILLING_CYCLE_LABELS[billingInfo.cycle].replace("every ", "")
		: "monthly";

	const limitIcon = React.useMemo(() => {
		switch (resourceType) {
			case "maps":
				return <MapIcon fontSize="var(--Icon-fontSize)" />;
			case "storage":
				return <HardDriveIcon fontSize="var(--Icon-fontSize)" />;
			case "private-viewers":
				return <UsersIcon fontSize="var(--Icon-fontSize)" />;
			default:
				return <WarningIcon fontSize="var(--Icon-fontSize)" />;
		}
	}, [resourceType]);

	const icon = React.useMemo(() => {
		if (isAddonConfirmation) {
			return <UserPlusIcon fontSize="var(--Icon-fontSize)" />;
		}
		if (isLimitReached || isLimitWithAddon) {
			return limitIcon;
		}
		return <LockIcon fontSize="var(--Icon-fontSize)" />;
	}, [isAddonConfirmation, isLimitReached, isLimitWithAddon, limitIcon]);

	const iconStyles = React.useMemo(() => {
		if (isAddonConfirmation) {
			return {
				bgcolor: "var(--mui-palette-success-50)",
				color: "var(--mui-palette-success-main)",
			};
		}
		return {
			bgcolor: "var(--mui-palette-info-50)",
			color: "var(--mui-palette-info-main)",
		};
	}, [isAddonConfirmation]);
	React.useEffect(() => {
		if (!addonType) return;
		setSeatQuantity(addonType === "editor-seat" ? 5 : 1);
	}, [addonType]);

	const partnerManagedUsageMessage = React.useMemo(() => {
		if (!usage || !resourceType) return "Contact your partner to unlock this.";
		const resourceName = RESOURCE_NAMES[resourceType];
		return `You are using ${usage.current} of ${usage.limit} ${resourceName}. Contact your partner to add more.`;
	}, [usage, resourceType]);

	if (isPartnerManaged) {
		return (
			<Dialog
				open={open}
				onClose={onClose}
				maxWidth="sm"
				fullWidth
				sx={{
					"& .MuiDialog-paper": {
						bgcolor: "transparent",
						boxShadow: "none",
						overflow: "visible",
					},
				}}>
				<Container maxWidth="sm">
					<Paper
						sx={{
							border: "1px solid var(--mui-palette-divider)",
							boxShadow: "var(--mui-shadows-16)",
						}}>
						<Stack
							spacing={3}
							sx={{ p: 3 }}>
							<Stack
								spacing={1}
								sx={{ alignItems: "center", textAlign: "center" }}>
								<Avatar
									sx={{
										bgcolor: "var(--mui-palette-info-50)",
										color: "var(--mui-palette-info-main)",
									}}>
									<LockIcon fontSize="var(--Icon-fontSize)" />
								</Avatar>
								<Typography variant="h5">{title}</Typography>
								<Typography
									color="text.secondary"
									variant="body2">
									{message}
								</Typography>
								{partnerManagedUsageMessage && (
									<Typography
										color="text.secondary"
										variant="body2">
										{partnerManagedUsageMessage}
									</Typography>
								)}
							</Stack>
							<Stack
								direction="row"
								justifyContent="flex-end">
								<Button
									color="secondary"
									variant="text"
									size="small"
									onClick={onClose}>
									Close
								</Button>
							</Stack>
						</Stack>
					</Paper>
				</Container>
			</Dialog>
		);
	}

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="sm"
			fullWidth
			sx={{
				"& .MuiDialog-paper": {
					bgcolor: "transparent",
					boxShadow: "none",
					overflow: "visible",
				},
			}}>
			<Container maxWidth="sm">
				<Paper
					sx={{
						border: "1px solid var(--mui-palette-divider)",
						boxShadow: "var(--mui-shadows-16)",
					}}>
					<Stack
						spacing={3}
						sx={{ p: 3 }}>
						<Stack
							spacing={1}
							sx={{ alignItems: "center", textAlign: "center" }}>
							<Avatar sx={iconStyles}>{icon}</Avatar>
							<Typography variant="h5">{title}</Typography>
							<Typography
								color="text.secondary"
								variant="body2">
								{message}
							</Typography>
						</Stack>

						<Stack
							spacing={2}
							sx={{
								alignItems: centerBody ? "center" : "stretch",
								textAlign: centerBody ? "center" : "left",
							}}>
							{(isLimitReached || isLimitWithAddon) && usage && (
								<UsageDisplay
									usage={usage}
									resourceType={resourceType}
								/>
							)}

							{isLimitWithAddon && addonBlockInfo && (
								<Box
									sx={{
										width: "100%",
										p: 2,
										bgcolor: "var(--mui-palette-background-default)",
										border: "1px solid var(--mui-palette-divider)",
										borderRadius: 1,
										textAlign: "left",
									}}>
									<Stack spacing={1.5}>
										<List
											disablePadding
											sx={{
												"& .MuiListItem-root": {
													justifyContent: "space-between",
													px: 0,
													py: 1.25,
												},
											}}>
											<ListItem
												divider
												sx={{ alignItems: "center" }}>
												<Stack
													direction="row"
													spacing={1}
													sx={{ alignItems: "center", flex: 1 }}>
													<Typography
														variant="body2"
														color="text.secondary">
														{ADDON_NAMES[addonBlockInfo.addonType]}
													</Typography>
													<FormControl
														size="small"
														sx={{ minWidth: 120 }}>
														<Select
															value={selectedQuantity}
															onChange={(e) =>
																setSelectedQuantity(e.target.value as number)
															}
															inputProps={{
																"aria-label": "Select quantity",
															}}>
															{quantityOptions.map((opt) => (
																<MenuItem
																	key={opt.value}
																	value={opt.value}>
																	{opt.label}
																</MenuItem>
															))}
														</Select>
													</FormControl>
												</Stack>
												<Typography
													variant="body2"
													color="text.secondary"
													sx={{ alignSelf: "center" }}>
													{formatCurrency(addonTotal)}/{billingCycleLabel}
												</Typography>
											</ListItem>
											<ListItem
												divider
												sx={{ py: 1.25, alignItems: "center" }}>
												<Typography
													variant="body2"
													sx={{ fontWeight: 600 }}>
													Due today (prorated)
												</Typography>
												<Typography
													variant="body2"
													sx={{ fontWeight: 600 }}>
													{formatCurrency(totalProrated)}
												</Typography>
											</ListItem>
											{billingInfo && (
												<PaymentMethodDisplay
													billingInfo={billingInfo}
													onManageBilling={onManageBilling}
												/>
											)}
										</List>
									</Stack>
								</Box>
							)}

							{showUpgradeOption && requiredPlan && (
								<Stack
									spacing={1}
									sx={{ width: "100%" }}>
									<Stack
										direction="row"
										justifyContent="space-between"
										alignItems="center">
										<Stack spacing={0.5}>
											<Typography variant="subtitle2">
												Or upgrade to {PLAN_NAMES[requiredPlan]}
											</Typography>
											<Typography
												variant="body2"
												color="text.secondary">
												Get {PLAN_LIMITS[requiredPlan].maps} maps and{" "}
												{PLAN_LIMITS[requiredPlan].storage}GB storage included
											</Typography>
										</Stack>
										<Button
											variant="text"
											size="small"
											endIcon={<ArrowRightIcon />}
											onClick={onUpgrade}>
											Upgrade
										</Button>
									</Stack>
								</Stack>
							)}

							{isAddonConfirmation && billingInfo && (
								<Box
									sx={{
										width: "100%",
										p: 2,
										bgcolor: "var(--mui-palette-background-default)",
										border: "1px solid var(--mui-palette-divider)",
										borderRadius: 1,
										textAlign: "left",
									}}>
									<List
										disablePadding
										sx={{
											"& .MuiListItem-root": {
												justifyContent: "space-between",
												px: 0,
												py: 1.25,
											},
										}}>
										<ListItem
											divider
											sx={{ alignItems: "center" }}>
											<Stack
												direction="row"
												spacing={1}
												sx={{ alignItems: "center", flex: 1 }}>
												<Typography
													variant="body2"
													color="text.secondary">
													{addonType ? ADDON_NAMES[addonType] : "Seat"}
												</Typography>
												<FormControl
													size="small"
													sx={{ minWidth: 110 }}>
													<Select
														value={seatQuantity}
														onChange={(e) =>
															setSeatQuantity(e.target.value as number)
														}
														inputProps={{
															"aria-label": "Select seat quantity",
														}}>
														{seatQuantityOptions.map((opt) => (
															<MenuItem
																key={opt.value}
																value={opt.value}>
																{opt.label}
															</MenuItem>
														))}
													</Select>
												</FormControl>
											</Stack>
											<Typography
												variant="body2"
												color="text.secondary"
												sx={{ alignSelf: "center" }}>
												{formatCurrency(seatTotal)}/{billingCycleLabel}
											</Typography>
										</ListItem>
										<ListItem
											divider
											sx={{ py: 1.25, alignItems: "center" }}>
											<Typography
												variant="body2"
												sx={{ fontWeight: 600 }}>
												Due today (prorated)
											</Typography>
											<Typography
												variant="body2"
												sx={{ fontWeight: 600 }}>
												{formatCurrency(seatProratedTotal)}
											</Typography>
										</ListItem>
									</List>
									<List
										disablePadding
										sx={{
											"& .MuiListItem-root": {
												justifyContent: "space-between",
												px: 0,
												py: 1.25,
											},
										}}>
										<PaymentMethodDisplay
											billingInfo={billingInfo}
											onManageBilling={onManageBilling}
											showTooltip
										/>
									</List>
								</Box>
							)}

							{isAddonConfirmation && !billingInfo && (
								<Typography
									variant="caption"
									color="text.secondary">
									{ADDON_BILLING_NOTE}
								</Typography>
							)}

							{!isAddonConfirmation && !isLimitWithAddon && requiredPlan && (
								<Stack
									spacing={1.5}
									sx={{ width: "100%" }}>
									<Typography
										variant="overline"
										color="text.secondary"
										sx={{ fontWeight: 600 }}>
										UPGRADE RECOMMENDED
									</Typography>
									<Stack
										direction="row"
										spacing={3}
										sx={{
											alignItems: "center",
											justifyContent: "center",
											width: "100%",
										}}>
										<Stack spacing={0.5}>
											<Typography variant="subtitle1">
												{PLAN_NAMES[currentPlan]}
											</Typography>
											<Chip
												label="CURRENT"
												size="small"
												variant="outlined"
												sx={{ color: "text.secondary" }}
											/>
										</Stack>
										<ArrowRightIcon color="var(--mui-palette-text-secondary)" />
										<Stack spacing={0.5}>
											<Typography variant="subtitle1">
												{PLAN_NAMES[requiredPlan]}
											</Typography>
											<Chip
												label="RECOMMENDED"
												size="small"
												color="success"
												variant="soft"
											/>
										</Stack>
									</Stack>
									<Stack
										direction="row"
										sx={{ justifyContent: "center" }}>
										<Link
											href="https://mangomap.com/pricing"
											target="_blank"
											rel="noopener noreferrer"
											color="text.secondary"
											underline="hover"
											sx={{
												display: "inline-flex",
												alignItems: "center",
												gap: 0.5,
												typography: "body2",
											}}>
											See all plans
											<ExternalLinkIcon size={14} />
										</Link>
									</Stack>
								</Stack>
							)}
						</Stack>

						<Stack
							direction="row"
							spacing={2}
							sx={{ alignItems: "center" }}>
							{showLeftDismiss && (
								<Button
									color="secondary"
									variant="text"
									size="small"
									onClick={onClose}>
									{leftDismissLabel}
								</Button>
							)}
							<Stack
								direction="row"
								spacing={2}
								sx={{ justifyContent: "flex-end", ml: "auto" }}>
								{!isNotNowCta && (
									<Button
										color="secondary"
										variant={isTrialStandard ? "outlined" : "text"}
										size="small"
										onClick={handleSecondaryAction}>
										{secondaryCta}
									</Button>
								)}
								<Button
									color="primary"
									variant="contained"
									onClick={handlePrimaryAction}>
									{primaryCtaLabel}
								</Button>
							</Stack>
						</Stack>
					</Stack>
				</Paper>
			</Container>
		</Dialog>
	);
}
